import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

// Standardized webhook for all service-related notifications
export const triggerServiceWebhook = async (
  type: 'service_request' | 'service_accepted' | 'service_reminder' | 'daily_summary' | 'service_reminder_test' | 'service_reschedule',
  payload: any,
  userId: string
) => {
  try {
     console.log(`Preparing ${type} webhook data:`, payload);
    
    // Fetch user profile information
    const { data: profileData, error: profileError } = await supabase
      .from('profiles')
      .select('first_name, company_name, preferred_messaging, whatsapp_number, telegram_chat_id, currency, email')
      .eq('id', userId)
      .single();
    
    if (profileError) {
      console.error("Error fetching profile data:", profileError);
      throw profileError;
    }

    // Prepare standardized payload
    const webhookPayload = {
      ...payload,
      event_type: type,
      timestamp: new Date().toISOString(),
      userId: userId,
      providerFirstName: profileData?.first_name || null,
      providerCompanyName: profileData?.company_name || null,
      providerTelegramChatId: profileData?.telegram_chat_id || null,
      providerPreferredMessaging: profileData?.preferred_messaging || "whatsapp",
      providerWhatsAppNumber: profileData?.whatsapp_number || null,
      providerCurrency: profileData?.currency || "KES",
      providerEmail: profileData?.email || null
    };
    
    console.log("Sending webhook payload:", webhookPayload);
    
    // Add notification to the database if it's a reminder
    if (type === 'service_reminder' || type === 'service_reminder_test') {
      try {
        // Cast to avoid TypeScript errors while Supabase types are updating
        const { error: notificationError } = await (supabase
          .from('notifications' as any)
          .insert({
            user_id: userId,
            type: 'reminder',
            title: 'Service Reminder',
            message: `Reminder sent to ${payload.customerName} for ${payload.serviceName}`,
            service_request_id: payload.requestId,
            read: false
          }) as any);
          
        if (notificationError) {
          console.error("Error creating notification:", notificationError);
        }
      } catch (notifyError) {
        console.error("Error creating reminder notification:", notifyError);
      }
    }

    const { data, error } = await supabase.functions.invoke('service-request-webhook', {
      body: JSON.stringify(webhookPayload),
    });
    
    if (error) {
      console.error(`Webhook error (${JSON.stringify(error)}):`);
      throw new Error(`Webhook error: ${JSON.stringify(error)}`);
    }
    
    console.log("Webhook response:", JSON.stringify(data));
    
    return { success: true, response: data };
  } catch (error) {
    console.error("Error triggering service webhook:", error);
    toast({
      variant: "destructive",
      title: "Notification Error",
      description: "Failed to send notification. Please try again.",
    });
    return { success: false, error: error.message || "Unknown error" };
  }
};

// Function to trigger a manual test of the daily summary
export const triggerDailySummaryTest = async (userId: string) => {
  try {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    const { data: bookings, error: bookingsError } = await supabase
      .from('service_requests')
      .select(`
        id,
        customer_name,
        customer_phone,
        customer_email,
        scheduled_at,
        notes,
        location,
        services:service_id (name, price)
      `)
      .eq('user_id', userId)
      .gte('scheduled_at', new Date().toISOString())
      .lt('scheduled_at', new Date(tomorrow.setHours(23, 59, 59)).toISOString())
      .not('status', 'in', '("rejected","cancelled")')
      .order('scheduled_at', { ascending: true });
      
    if (bookingsError) {
      console.error("Error fetching bookings:", bookingsError);
      throw bookingsError;
    }
    
    // Transform to the format expected by the webhook
    const summaryData = {
      summary_date: tomorrow.toISOString().split('T')[0],
      bookings: bookings.map(booking => ({
        request_id: booking.id,
        customer_name: booking.customer_name,
        customer_phone: booking.customer_phone || "Not provided",
        customer_email: booking.customer_email || "Not provided",
        scheduled_at: booking.scheduled_at,
        notes: booking.notes || "None",
        location: booking.location || "Not specified",
        service_name: booking.services.name,
        service_price: booking.services.price
      }))
    };
    
    return await triggerServiceWebhook('daily_summary', summaryData, userId);
  } catch (error) {
    console.error("Error triggering daily summary test:", error);
    toast({
      variant: "destructive",
      title: "Test Failed",
      description: "Could not trigger daily summary test. See console for details.",
    });
    return { success: false, error: error.message || "Unknown error" };
  }
};

// Function to send a service reminder to a customer
export const sendServiceReminder = async (requestId: string, userId: string) => {
  try {
    // Fetch request details with service information
    const { data: request, error: requestError } = await supabase
      .from('service_requests')
      .select(`
        id,
        customer_name,
        customer_phone,
        customer_email,
        scheduled_at,
        notes,
        location,
        services:service_id (name, price)
      `)
      .eq('id', requestId)
      .single();
      
    if (requestError) {
      console.error("Error fetching request:", requestError);
      throw requestError;
    }
    
    // Get the provider's email
    const { data: userData, error: userError } = await supabase
      .from('profiles')
      .select('email')
      .eq('id', userId)
      .single();
      
    if (userError) {
      console.error("Error fetching provider email:", userError);
    }
    
    // Format reminder data
    const reminderData = {
      requestId: request.id,
      serviceName: request.services.name,
      servicePrice: request.services.price.toString(),
      customerName: request.customer_name,
      customerPhone: request.customer_phone || "Not provided",
      customerEmail: request.customer_email || "Not provided",
      appointmentDate: request.scheduled_at,
      formattedAppointmentDate: request.scheduled_at ? new Date(request.scheduled_at).toLocaleString() : "Not scheduled",
      specialRequests: request.notes || "None",
      location: request.location || "Not specified",
      providerEmail: userData?.email || null
    };
    
    // Create an in-app notification for this reminder
    try {
      const { error: notificationError } = await (supabase
        .from('notifications' as any)
        .insert({
          user_id: userId,
          type: 'reminder',
          title: 'Reminder Sent',
          message: `Reminder sent to ${request.customer_name} for ${request.services.name}`,
          service_request_id: request.id,
          read: false
        }) as any);
        
      if (notificationError) {
        console.error("Error creating notification:", notificationError);
      }
    } catch (notifyError) {
      console.error("Error creating manual reminder notification:", notifyError);
    }
    
    const result = await triggerServiceWebhook('service_reminder', reminderData, userId);
    
    if (result.success) {
      toast({
        title: "Reminder Sent",
        description: `Reminder sent to ${request.customer_name}`,
      });
    }
    
    return result;
  } catch (error) {
    console.error("Error sending service reminder:", error);
    toast({
      variant: "destructive",
      title: "Reminder Failed",
      description: "Could not send service reminder. Please try again.",
    });
    return { success: false, error: error.message || "Unknown error" };
  }
};
